import {z} from 'zod';

import {Locale} from '@dms/api/base';
import {UuidResponseBody} from '@dms/api/shared';

const LOCALES = [
  'cs-CZ',
  'de-DE',
  'de-BE',
  'en-GB',
  'en-US',
  'es-ES',
  'fr-FR',
  'fr-BE',
  'it-IT',
  'pl-PL',
  'sk-SK',
  'nl-BE',
  'nl-NL',
] as const satisfies readonly Locale[];

/**
 * Locale code for a warranty terms-and-conditions entry.
 *
 * `LOCALES` mirrors the backend `Locale` enum (a required `string` constrained to
 * these 13 values), verified against the OpenAPI spec. The frontend only ever
 * submits the tenant's enabled content-language codes (see `ContentLanguageCode`
 * in `@dms/api/settings`), which are a subset of this enum. Keep this array in
 * sync if the backend enum changes.
 */
export const WarrantyTermsAndConditionsRequestBodySchema = z.object({
  text: z.string(),
  locale: z.enum(LOCALES),
});
export type WarrantyTermsAndConditionsRequestBody = z.infer<
  typeof WarrantyTermsAndConditionsRequestBodySchema
>;

export const WarrantyProgramRequestBodySchema = z.object({
  name: z.string(),
  duration: z.number().int().min(1).nullish(),
  watermarkId: z.string().uuid().nullable(),
  termsAndConditions: z.array(WarrantyTermsAndConditionsRequestBodySchema),
  // Update only: opt-in to re-publish the program's advertised vehicles when its watermark changed.
  republishAds: z.boolean().optional(),
});
export type WarrantyProgramRequestBody = z.infer<typeof WarrantyProgramRequestBodySchema>;

// List response — summary only (id, name, duration).
export const WarrantyProgramListItemResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  duration: z.number().nullable(),
});
export type WarrantyProgramListItemResponseBody = z.infer<
  typeof WarrantyProgramListItemResponseBodySchema
>;

export const ListWarrantyProgramApiResponseSchema = z.array(
  WarrantyProgramListItemResponseBodySchema
);
export type ListWarrantyProgramApiResponse = z.infer<typeof ListWarrantyProgramApiResponseSchema>;

// Detail response (GET /dms/v1/warranty-program/{id}). Verified against the
// sales_context OpenAPI spec (`WarrantyProgramResponseBody`): every field below is
// required by the backend. `active`, `createdAt`, and `createdBy` are read-only
// metadata that the write contract (`WarrantyProgramRequestBody`) does not carry,
// so they must be declared here or the response schema silently strips them.
export const WarrantyProgramResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  duration: z.number().nullable(),
  watermarkId: z.string().uuid().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
  createdBy: z.string(),
  termsAndConditions: z.array(WarrantyTermsAndConditionsRequestBodySchema),
});
export type WarrantyProgramResponseBody = z.infer<typeof WarrantyProgramResponseBodySchema>;

export type GetWarrantyProgramApiArg = {
  warrantyProgramId: string;
};

export type CreateWarrantyProgramApiArg = {
  createWarrantyProgramRequestBody: WarrantyProgramRequestBody;
};

// POST /dms/v1/warranty-program returns 201 with the new program id
// (`UuidResponseBody` in the spec). Reuse the shared schema rather than
// redeclaring `{id: string}`.
export type CreateWarrantyProgramApiResponse = UuidResponseBody;

export type UpdateWarrantyProgramApiArg = {
  warrantyProgramId: string;
  updateWarrantyProgramRequestBody: WarrantyProgramRequestBody;
};

export type DeleteWarrantyProgramApiArg = {
  warrantyProgramId: string;
};

// Sales warranty settings (page-level form): GET / PUT /dms/v1/settings/sales/warranty.
export const SalesWarrantySettingsRequestBodySchema = z.object({
  defaultDuration: z.number().int().min(1).nullable(),
  warrantyProgramDurationOverrideDisabled: z.boolean(),
});
export type SalesWarrantySettingsRequestBody = z.infer<
  typeof SalesWarrantySettingsRequestBodySchema
>;

// Read contract is identical to the write contract today, but kept separate so the
// two can diverge safely (e.g. future read-only fields like `updatedAt`) without
// the write schema silently stripping them from the response.
export const SalesWarrantySettingsResponseBodySchema = SalesWarrantySettingsRequestBodySchema;
export type SalesWarrantySettingsResponseBody = z.infer<
  typeof SalesWarrantySettingsResponseBodySchema
>;

export type SetSalesWarrantySettingsApiArg = {
  salesWarrantySettingsRequestBody: SalesWarrantySettingsRequestBody;
};

// Warranty types (dictionary): GET/POST /dms/v1/warranty-type, GET/PUT/DELETE
// /dms/v1/warranty-type/{id}. The backend names this entity "WarrantyProduct" in the
// OpenAPI spec, but the data grid and the FE surface call it `warranty-type`.
export const WarrantyTypeCodeSchema = z.enum([
  'MECHANICAL',
  'PAINT_AND_SURFACE_CORROSION',
  'ANTI_PERFORATION',
  'HYBRID',
  'HV_BATTERY_LIMITED',
  'HV_BATTERY_CAPACITY_LIMITED',
  'POWERTRAIN',
  'EXTENDED_WARRANTY',
  'GAP_INSURANCE',
  'TIRE_AND_WHEEL',
  'ACCESSORIES',
  'OTHER',
]);
export type WarrantyTypeCode = z.infer<typeof WarrantyTypeCodeSchema>;

export const WarrantyTypeSourceSchema = z.enum(['OEM', 'DEALER']);
export type WarrantyTypeSource = z.infer<typeof WarrantyTypeSourceSchema>;

export const WarrantyTypeRequestBodySchema = z.object({
  warrantyType: WarrantyTypeCodeSchema,
  durationMonths: z.number().nullable(),
  durationKm: z.number().nullable(),
  description: z.string(),
  source: WarrantyTypeSourceSchema,
  externalId: z.string().nullable(),
});
export type WarrantyTypeRequestBody = z.infer<typeof WarrantyTypeRequestBodySchema>;

// Detail/list response = request contract plus the server-assigned id.
export const WarrantyTypeResponseBodySchema = WarrantyTypeRequestBodySchema.extend({
  id: z.string(),
});
export type WarrantyTypeResponseBody = z.infer<typeof WarrantyTypeResponseBodySchema>;

export const ListWarrantyTypeApiResponseSchema = z.array(WarrantyTypeResponseBodySchema);
export type ListWarrantyTypeApiResponse = z.infer<typeof ListWarrantyTypeApiResponseSchema>;

export type GetWarrantyTypeApiArg = {
  warrantyTypeId: string;
};

export type CreateWarrantyTypeApiArg = {
  createWarrantyTypeRequestBody: WarrantyTypeRequestBody;
};

// POST /dms/v1/warranty-type returns 201 with the new id (`UuidResponseBody`).
export type CreateWarrantyTypeApiResponse = UuidResponseBody;

export type UpdateWarrantyTypeApiArg = {
  warrantyTypeId: string;
  updateWarrantyTypeRequestBody: WarrantyTypeRequestBody;
};

export type DeleteWarrantyTypeApiArg = {
  warrantyTypeId: string;
};

// Vehicle warranty assignments: a warranty product assigned to a specific vehicle,
// keyed by `warrantyProductId`. GET/POST /dms/v1/vehicle/{vehicleId}/warranty,
// PUT/DELETE /dms/v1/vehicle/{vehicleId}/warranty/{warrantyProductId}.
export const AssignWarrantyProductRequestBodySchema = z.object({
  warrantyProductId: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  startOdometerKm: z.number().nullable(),
  endOdometerKm: z.number().nullable(),
  isReadOnly: z.boolean(),
});
export type AssignWarrantyProductRequestBody = z.infer<
  typeof AssignWarrantyProductRequestBodySchema
>;

// The product cannot be changed on edit, so the update contract omits it.
export const UpdateWarrantyAssignmentRequestBodySchema = z.object({
  startDate: z.string(),
  endDate: z.string().nullable(),
  startOdometerKm: z.number().nullable(),
  endOdometerKm: z.number().nullable(),
});
export type UpdateWarrantyAssignmentRequestBody = z.infer<
  typeof UpdateWarrantyAssignmentRequestBodySchema
>;

// List item = assignment fields plus the resolved warranty-product (type) fields.
export const AssignedWarrantyResponseBodySchema = z.object({
  warrantyProductId: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  startOdometerKm: z.number().nullable(),
  endOdometerKm: z.number().nullable(),
  isReadOnly: z.boolean(),
  warrantyType: WarrantyTypeCodeSchema,
  durationMonths: z.number().nullable(),
  durationKm: z.number().nullable(),
  description: z.string(),
  source: WarrantyTypeSourceSchema,
  externalId: z.string().nullable(),
});
export type AssignedWarrantyResponseBody = z.infer<typeof AssignedWarrantyResponseBodySchema>;

export const ListVehicleWarrantiesApiResponseSchema = z.array(AssignedWarrantyResponseBodySchema);
export type ListVehicleWarrantiesApiResponse = z.infer<
  typeof ListVehicleWarrantiesApiResponseSchema
>;

export type ListVehicleWarrantiesApiArg = {
  vehicleId: string;
};

export type AssignVehicleWarrantyApiArg = {
  vehicleId: string;
  assignWarrantyProductRequestBody: AssignWarrantyProductRequestBody;
};

export type AssignVehicleWarrantyApiResponse = UuidResponseBody;

export type UpdateVehicleWarrantyApiArg = {
  vehicleId: string;
  warrantyProductId: string;
  updateWarrantyAssignmentRequestBody: UpdateWarrantyAssignmentRequestBody;
};

export type RemoveVehicleWarrantyApiArg = {
  vehicleId: string;
  warrantyProductId: string;
};
